import { CompositeScreenProps, useRoute } from "@react-navigation/native"
import { observer } from "mobx-react-lite"
import * as React from "react"
import { Alert, Modal, Platform, StyleProp, ViewStyle } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import Share from '../share/share'
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import { captureRef } from 'react-native-view-shot'
import { translate } from "../../i18n"
import { color } from "../../theme"
import { Button } from "../button/button"
import { InstagramStoryPostLayout } from "../instagram-story-post-layout/instagram-story-post-layout"
import { JalaIcon } from "../jala-icon/jala-icon"
import { Text } from "../text/text"
import { requestWriteExternalStoragePermission } from "../../utils/permissions"
import { Toast } from "../toast/toast"
import { View } from 'native-base'
import JalaAnalytic from "../../utils/jala-analytic"
import { HomeNavigatorParamList } from "../../navigators"


const CONTAINER = (height): ViewStyle => ({
  justifyContent: "space-between",
  backgroundColor: color.primary,
  flexDirection: "row",
  height: height
})

const HEADER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center'
}

const MARGIN = (width):ViewStyle => ({
  marginRight: width
})

const BACK_BUTTON: ViewStyle = {
  marginLeft: 16,
  marginRight: 12
}


export interface HeaderWithShareProps {
  /**
   * An optional style override useful for padding & margin.
   */
  style?: StyleProp<ViewStyle>
  colorScheme?: "light" | "dark"
  title?: string
  navigation?: any
  instagramStory?: boolean
  sharedData?: any
}

/**
 * Describe your component here
 */
export const HeaderWithShare: React.FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps <any, any> & HeaderWithShareProps> = observer(function HeaderWithShare(props: HeaderWithShareProps) {
  const { colorScheme, title, navigation, instagramStory } = props
  const route = useRoute();

  const backIcon = Platform.OS === "ios" ? 'arrow_back_ios' : 'arrow_back';
  const instagramStoryPostRef = React.useRef(null)

  const headerHeight = 55.8;
  const { sharedData: { post } } = props;

  const [ instagramStoryPostLoad, setInstagramStoryPostLoad ] = React.useState<boolean>(false);

  const onShare = async () => {
    // const { sharedData: { title, message }, mixpanelKey } = props;
    const { sharedData: { title, message, url } } = props;

    try {
      if (Platform.OS === "web") {
        if ('share' in window.navigator)
          await window.navigator.share({ title, text: message, url })
        if ('clipboard' in window.navigator)
          await window.navigator.clipboard.writeText(url).then(() => {
            Toast.show({ title: 'Copied to clipboard' })
          })
      } else {
        await Share.open({ message: `${title} ${message}`, failOnCancel: false })
      }
      switch (route.name as keyof HomeNavigatorParamList) {
        case "Home.ShrimpPrice.Detail":
          JalaAnalytic.log("Mobile app: share post - detail price"); break;
        case "Home.News.Detail":
          JalaAnalytic.log("Mobile app: share post - detail news"); break;
        case "Home.Disease.Detail":
          JalaAnalytic.log("Mobile app: share post - detail disease"); break;
      }
    } catch (error) {
      Alert.alert(translate('failed'), translate('failedShared'), [{ text: 'OK' }]);
    }
  }

  const onInstagramStoryShare = async () => {
    const { sharedData: { post } } = props;
    if (post && post.id) setInstagramStoryPostLoad(true);
  }

  const  onInstagramStoryPostLayoutLoadEnd = async () => {
    // const { sharedData: { title, message }, mixpanelKey } = this.props;
    const { sharedData: { title, message } } = props;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    captureRef(instagramStoryPostRef, { result: 'data-uri' })
      .then(async (imgBase64) => {
        if (Platform.OS === 'android') {
          const result = await requestWriteExternalStoragePermission();
          if (!result) return;
        }
        const shareOptions = {
          // social: Share.Social.INSTAGRAM_STORIES,
          message: `${title} ${message}`,
          url: imgBase64
        };
        await Share.open(shareOptions)
          .catch(() => {
            setInstagramStoryPostLoad(false);
          })
          // .finally(() => JalaAnalytic.log(mixpanelKey));
      })
      .finally(() => setInstagramStoryPostLoad(false));
  }

  return (<View backgroundColor={color.primary}>
    <SafeAreaView edges={["top"]} />
    <View style={CONTAINER(headerHeight)}>
      <View style={HEADER}>
        <Button testID="jala:id/back-button" preset="link" textPreset="display1" style={BACK_BUTTON} colorScheme={colorScheme} onPress={navigation.goBack}>
          <JalaIcon name={backIcon} size={25} color={color.palette.white} />
        </Button>
        <Text text={title} preset="title2" color={color.palette.white} />
      </View>
      <View style={HEADER}>
        {instagramStory && Platform.OS !== 'web' && post && post.id && (
          <Button testID="jala:id/instagram-button" preset="link" style={MARGIN(24)} onPress={onInstagramStoryShare}>
            <FontAwesomeIcon name="instagram" size={25} color={color.palette.white} />
          </Button>
        )}
        {post && post.id && (
          <Button testID="jala:id/share-button" preset="link" style={MARGIN(12)} onPress={onShare}>
            <JalaIcon name="share" size={25} color={color.palette.white} />
          </Button>
        )}
      </View>
      {instagramStory && instagramStoryPostLoad && post && post.id && (
        <Modal>
          <InstagramStoryPostLayout current={instagramStoryPostRef} forwardRef={instagramStoryPostRef} post={post} onLoadEnd={onInstagramStoryPostLayoutLoadEnd} />
        </Modal>
      )}
    </View>
  </View>)
})
